import { Alert, Button, Card, Tag, Text } from '@nimbus-ds/components';
import { CheckIcon, LinkOffIcon } from '@nimbus-ds/icons';
import { Trans } from 'react-i18next';
import { useLocation } from 'react-router';
import { SOCIAL_GA4_DATA_API_OAUTH_DISABLED } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { Stack } from 'commons/components';
import { useToastStatus } from 'commons/hooks';
import { StatusType } from 'commons/types';
import { openWindow } from 'commons/utils/window';
import { useIsMobileDevice } from 'domains/Auth/hooks';
import { ga4DataApiOAuthButtonClick } from 'domains/Configurations/ExternalCodes/tracking';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import { useStoreInfo } from 'domains/PartnersApps/hooks';

const ga4ParamToStatus = (locationSearch): StatusType => {
  if (locationSearch.includes('ga4Success')) {
    return 'success';
  }

  return locationSearch.includes('ga4ErrorCode') ? 'error' : 'idle';
};

interface LinkedGoogleAnalyticsProps {
  googleAnalyticsApiSecret: string;
  googleAnalyticsMeasurementId: string;
  ga4DataApiOAuthGiven: boolean;
  onClick: () => void;
}

export function LinkedGoogleAnalytics({
  googleAnalyticsApiSecret,
  googleAnalyticsMeasurementId,
  ga4DataApiOAuthGiven,
  onClick,
}: Readonly<LinkedGoogleAnalyticsProps>) {
  const t = useTranslationConfigurations(
    'externalCodes.google.googleAnalytics.linked',
  );
  const location = useLocation();
  const isMobileDevice = useIsMobileDevice();
  const { id: storeId } = useStoreInfo();
  const [hasSocialGA4DataApiOauthDisabledTag] = useHasTags(
    SOCIAL_GA4_DATA_API_OAUTH_DISABLED,
  );

  useToastStatus({
    status: ga4ParamToStatus(location.search),
    success: t('authorizationConsent.success'),
    error: t('authorizationConsent.error'),
  });

  const redirectToConsentPage = () => {
    ga4DataApiOAuthButtonClick(googleAnalyticsMeasurementId);
    openWindow(
      `${
        import.meta.env.VITE_MS_SOCIAL_HOST
      }/${storeId}/ga4/start-consent?redirectUrl=${encodeURIComponent(
        window.location.href,
      )}`,
      false,
    );
  };

  return (
    <>
      <Alert
        appearance="warning"
        title={t('authorizationConsent.title')}
        show={
          !isMobileDevice &&
          !ga4DataApiOAuthGiven &&
          !hasSocialGA4DataApiOauthDisabledTag
        }
      >
        <Text>{t('authorizationConsent.message')}</Text>
        <Button onClick={redirectToConsentPage}>
          {t('authorizationConsent.button')}
        </Button>
      </Alert>
      <Card padding="small">
        <Stack column align="stretch">
          <Tag appearance="success">
            <CheckIcon />
            <Text fontSize="caption">{t('tag')}</Text>
          </Tag>

          <Text fontSize="highlight">
            <Trans
              t={t}
              i18nKey="googleAnalyticsMeasurementId"
              values={{ googleAnalyticsMeasurementId }}
              components={{
                bold: <strong />,
              }}
            />
          </Text>
          <Text fontSize="highlight">
            <Trans
              t={t}
              i18nKey="googleAnalyticsApiSecret"
              values={{ googleAnalyticsApiSecret }}
              components={{
                bold: <strong />,
              }}
            />
          </Text>
          <Button onClick={onClick} appearance="neutral">
            <LinkOffIcon />
            {t('unlinked')}
          </Button>
        </Stack>
      </Card>
    </>
  );
}
